import { Quote, ExternalLink, Phone, Mail, MapPin, Globe, Building2, ArrowRight, ArrowUpRight, GripVertical, Trash2, Plus, Columns2, Columns3, Columns4, Square, Type, List as ListIcon, Image as ImageIcon, Images, Briefcase, FileText, Star, Minus, SlidersHorizontal, Play, Pause, Music2 } from "lucide-react";
import { Img } from "./Common";
import { lazyImgs } from "../../lib/html";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, rectSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useState, useRef, useEffect } from "react";

/* =====================================================
   Shared design tokens
   ===================================================== */

// Legacy "GLASS_CELL" + "GLASS_PAD" stay exported for callers that render
// outside of ServiceBlocksList (e.g. the admin BlockBuilder preview).
export const GLASS_CELL =
  "relative rounded-2xl border border-white/15 bg-white/10 backdrop-blur-md shadow-[0_8px_40px_rgba(0,0,0,0.25)] " +
  "ring-1 ring-inset ring-white/10 overflow-hidden";
export const GLASS_PAD = "p-5 sm:p-7";

// --- Per-block style tokens ---------------------------------------------------
// block.style = { bg: 0-100, border: 'none'|'subtle'|'accent', border_color?: hex,
//                 shadow: bool, blur: 'none'|'sm'|'md'|'lg', radius: 'md'|'lg'|'xl'|'2xl' }
const BLUR_CLS = { none: "", sm: "backdrop-blur-sm", md: "backdrop-blur-md", lg: "backdrop-blur-lg" };
const RADIUS_CLS = { md: "rounded-md", lg: "rounded-lg", xl: "rounded-xl", "2xl": "rounded-2xl" };
function cellClassAndStyle(style) {
  // Default: completely borderless, transparent, no shadow — just content.
  // Only when the block explicitly sets a non-empty style do we apply a frame.
  const s = style && typeof style === "object" && Object.keys(style).length > 0 ? style : null;
  if (!s) {
    return { cls: "relative", style: {} };
  }
  const bg = Math.max(0, Math.min(100, Number(s.bg ?? 0)));
  const blurKey = s.blur || "none";
  const radiusKey = s.radius || "2xl";
  const borderMode = s.border || "none";                        // 'none' | 'subtle' | 'accent'
  const shadow = s.shadow === true ? "shadow-[0_8px_40px_rgba(0,0,0,0.25)]" : "";
  const borderBase = borderMode === "none"
    ? "border border-transparent"
    : borderMode === "accent"
      ? "border border-s-primary/60 ring-1 ring-inset ring-s-primary/20"
      : "border border-white/15 ring-1 ring-inset ring-white/10";
  const cls = `relative ${RADIUS_CLS[radiusKey] || RADIUS_CLS["2xl"]} overflow-hidden ${BLUR_CLS[blurKey] || ""} ${borderBase} ${shadow}`;
  const inline = bg > 0 ? { backgroundColor: `rgba(255, 255, 255, ${bg / 100})` } : {};
  if (borderMode !== "none" && s.border_color) inline.borderColor = s.border_color;
  return { cls, style: inline };
}

// --- Span helpers -------------------------------------------------------------
// Legacy string presets still accepted; numeric 1-12 is the new canonical form.
const PRESET_COLS = { full: 12, two_thirds: 8, half: 6, third: 4, quarter: 3 };
const COL_CLASS = {
  1: "md:col-span-1", 2: "md:col-span-2", 3: "md:col-span-3", 4: "md:col-span-4",
  5: "md:col-span-5", 6: "md:col-span-6", 7: "md:col-span-7", 8: "md:col-span-8",
  9: "md:col-span-9", 10: "md:col-span-10", 11: "md:col-span-11", 12: "md:col-span-12",
};
function spanToCols(s) {
  if (typeof s === "number") return Math.max(1, Math.min(12, Math.round(s)));
  if (typeof s === "string") {
    if (PRESET_COLS[s]) return PRESET_COLS[s];
    if (/^\d+$/.test(s)) return Math.max(1, Math.min(12, parseInt(s, 10)));
  }
  return 12;
}
function spanClass(s) { return COL_CLASS[spanToCols(s)]; }
// Back-compat alias used elsewhere in the module.
const SPAN_COLS = { full: COL_CLASS[12], two_thirds: COL_CLASS[8], half: COL_CLASS[6], third: COL_CLASS[4], quarter: COL_CLASS[3] };

/* =====================================================
   Reusable sub-cards
   ===================================================== */

export function CaseCard({ c, t }) {
  // Prefer card_title (short list title) over the full title when provided
  const cardTitle = t(c.card_title) || t(c.title);
  const summary = t(c.summary);
  const Wrap = c.external_url ? "a" : "div";
  const props = c.external_url ? { href: c.external_url, target: "_blank", rel: "noopener noreferrer" } : {};
  return (
    <Wrap
      {...props}
      className="group block rounded-xl overflow-hidden border border-white/15 bg-white/10 backdrop-blur-md hover:bg-white/[0.14] hover:-translate-y-0.5 transition-all shadow-[0_6px_30px_rgba(0,0,0,0.2)]"
      data-testid="service-case-card"
    >
      {c.cover && (
        <div className="aspect-video overflow-hidden">
          <Img src={c.cover} alt={cardTitle} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        </div>
      )}
      <div className="p-5">
        {c.year && <p className="text-[11px] uppercase tracking-widest text-white/50 mb-2">{c.year}</p>}
        <h4 className="text-lg leading-snug font-semibold tracking-tight" style={{ color: "var(--s-primary)" }}>{cardTitle}</h4>
        {summary && <p className="mt-2 text-sm text-white/80 leading-relaxed line-clamp-3">{summary}</p>}
        {c.external_url && (
          <span className="inline-flex items-center gap-1 mt-3 text-xs" style={{ color: "var(--s-primary)" }}>
            <ExternalLink size={12} />Projeyi incele
          </span>
        )}
      </div>
    </Wrap>
  );
}

export function TestimonialCard({ q, t }) {
  const text = t(q.text);
  return (
    <figure
      className="relative rounded-xl border border-white/15 bg-white/10 backdrop-blur-md p-6 shadow-[0_6px_30px_rgba(0,0,0,0.2)]"
      data-testid="service-testimonial"
    >
      <Quote size={22} className="text-s-primary/50 absolute top-5 right-5" aria-hidden />
      <blockquote className="text-sm leading-relaxed text-white/90">"{text}"</blockquote>
      <figcaption className="mt-5 flex items-center gap-3">
        {q.logo ? (
          <Img src={q.logo} alt={q.company || q.name} className="w-10 h-10 rounded-full object-cover border border-white/15" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-s-primary/20 flex items-center justify-center font-heading text-s-primary">
            {(q.name || "?").slice(0, 1)}
          </div>
        )}
        <div>
          <p className="font-semibold text-sm text-white">{q.name}</p>
          <p className="text-xs text-white/50">{[q.role, q.company].filter(Boolean).join(" · ")}</p>
        </div>
      </figcaption>
    </figure>
  );
}

/* =====================================================
   Block renderers (content-only; no outer section/max-w)
   ===================================================== */

function BlockHeading({ title, t }) {
  const txt = t(title);
  if (!txt) return null;
  return <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-5 text-white">{txt}</h2>;
}

function HeadingBlock({ block, t }) {
  const txt = t(block.title);
  if (!txt) return null;
  const level = block.data?.level || "h2";
  const align = block.data?.align || "left";
  const alignCls = align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left";
  const common = `font-heading font-bold tracking-tight ${alignCls}`;
  const style = { color: "var(--s-primary)" };
  if (level === "h4") return <h4 className={`${common} text-base sm:text-lg`} style={style}>{txt}</h4>;
  if (level === "h3") return <h3 className={`${common} text-lg sm:text-xl`} style={style}>{txt}</h3>;
  return <h2 className={`${common} text-2xl sm:text-3xl`} style={style}>{txt}</h2>;
}

function TextBlock({ block, t }) {
  const html = t(block.data?.html) || "";
  return (
    <>
      <BlockHeading title={block.title} t={t} />
      <div className="prose-site prose-lg prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: lazyImgs(html) }} />
    </>
  );
}

function ListBlock({ block, t }) {
  const items = Array.isArray(block.data?.items) ? block.data.items : [];
  const isNumbered = block.data?.style === "numbered";
  return (
    <>
      <BlockHeading title={block.title} t={t} />
      {isNumbered ? (
        <ol className="list-decimal pl-6 space-y-2 text-base text-white/85">
          {items.map((it, i) => <li key={i}>{typeof it === "string" ? it : t(it)}</li>)}
        </ol>
      ) : (
        <ul className="space-y-2 text-white/85">
          {items.map((it, i) => (
            <li key={i} className="flex items-start gap-2.5 text-base">
              <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-s-primary shrink-0" />
              <span>{typeof it === "string" ? it : t(it)}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function ImageBlock({ block, t }) {
  const src = block.data?.src;
  if (!src) return null;
  const align = block.data?.align || "center";
  const alignCls = align === "left" ? "mr-auto" : align === "right" ? "ml-auto" : "mx-auto";
  const caption = t(block.data?.caption);
  const h = Number(block.data?.height || 0);
  const style = h > 0 ? { height: `${h}px` } : {};
  const imgCls = h > 0 ? "w-full h-full object-cover rounded-xl" : "w-full h-auto rounded-xl";
  return (
    <>
      <BlockHeading title={block.title} t={t} />
      <figure className={`${alignCls}`} style={style}>
        <Img src={src} alt={caption || "görsel"} className={imgCls} />
        {caption && <figcaption className="mt-2 text-xs text-white/50 text-center">{caption}</figcaption>}
      </figure>
    </>
  );
}

function GalleryBlock({ block, t }) {
  const items = Array.isArray(block.data?.items) ? block.data.items.filter((x) => x && x.src) : [];
  if (!items.length) return null;
  const cols = Number(block.data?.cols || 3);
  const gridCols = cols === 2 ? "sm:grid-cols-2" : cols === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-2 lg:grid-cols-3";
  return (
    <>
      <BlockHeading title={block.title} t={t} />
      <div className={`grid grid-cols-1 ${gridCols} gap-4`}>
        {items.map((it, i) => (
          <figure key={i} className="rounded-lg overflow-hidden border border-white/10 bg-white/[0.03]">
            <Img src={it.src} alt={t(it.caption) || `görsel ${i + 1}`} className="w-full aspect-[4/3] object-cover" />
            {t(it.caption) && <figcaption className="p-2 text-xs text-white/60">{t(it.caption)}</figcaption>}
          </figure>
        ))}
      </div>
    </>
  );
}

/* ---------------- Media helpers ---------------- */
const YT_RE = /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;
const VIMEO_RE = /vimeo\.com\/(?:video\/|channels\/[\w-]+\/|groups\/[\w-]+\/videos\/)?(\d{6,12})/;
const DM_RE = /(?:dailymotion\.com\/(?:video|embed\/video)\/|dai\.ly\/)([a-zA-Z0-9]{5,10})/;
const SC_RE = /soundcloud\.com\//i;
const SPOT_RE = /open\.spotify\.com\//i;

function detectVideo(url) {
  if (!url) return null;
  let m = url.match(YT_RE);
  if (m) return { kind: "iframe", src: `https://www.youtube-nocookie.com/embed/${m[1]}?rel=0` };
  m = url.match(VIMEO_RE);
  if (m) return { kind: "iframe", src: `https://player.vimeo.com/video/${m[1]}` };
  m = url.match(DM_RE);
  if (m) return { kind: "iframe", src: `https://geo.dailymotion.com/player.html?video=${m[1]}` };
  if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(url)) return { kind: "file", src: url };
  if (url.startsWith("/api/video-files/")) return { kind: "file", src: url };
  return null;
}

function detectAudio(url) {
  if (!url) return null;
  if (SPOT_RE.test(url)) {
    const m = url.match(/open\.spotify\.com\/(track|album|playlist|episode)\/([a-zA-Z0-9]+)/);
    if (m) return { kind: "iframe", src: `https://open.spotify.com/embed/${m[1]}/${m[2]}` };
  }
  if (SC_RE.test(url)) {
    return { kind: "iframe", src: `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=%23d4af37&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=true` };
  }
  if (/\.(mp3|wav|ogg|m4a|aac)(\?|$)/i.test(url)) return { kind: "file", src: url };
  if (url.startsWith("/api/audio-files/")) return { kind: "file", src: url };
  // Fallback — assume direct audio
  return { kind: "file", src: url };
}

function fmtTime(s) {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

function AudioPlayer({ src, title, artist, cover, style = "card" }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCur(a.currentTime);
    const onLoaded = () => setDur(a.duration || 0);
    const onEnd = () => setPlaying(false);
    const onPause = () => setPlaying(false);
    const onPlay = () => setPlaying(true);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("ended", onEnd);
    a.addEventListener("pause", onPause);
    a.addEventListener("play", onPlay);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("play", onPlay);
    };
  }, [src]);
  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
    } else {
      // Pause every other audio on the page so only one plays at a time
      document.querySelectorAll("audio").forEach((el) => { if (el !== a) el.pause(); });
      a.play().catch(() => {});
    }
  };
  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    a.currentTime = pct * dur;
    setCur(a.currentTime);
  };
  const progress = dur > 0 ? (cur / dur) * 100 : 0;
  const commonAudio = <audio ref={audioRef} src={src} preload="metadata" />;

  if (style === "strip") {
    return (
      <div className="flex items-center gap-4 rounded-full border border-white/15 bg-white/5 backdrop-blur-md px-4 py-2.5" data-testid="audio-player-strip">
        {commonAudio}
        <button
          type="button"
          onClick={toggle}
          className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition hover:scale-105"
          style={{ background: "var(--s-primary)", color: "var(--s-onprimary)" }}
          data-testid="audio-play-btn"
          aria-label={playing ? "Duraklat" : "Oynat"}
        >
          {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-white">{title || "Parça"}</div>
          {artist && <div className="truncate text-xs text-white/60">{artist}</div>}
        </div>
        <button type="button" onClick={seek} className="relative hidden sm:block h-1 w-32 bg-white/15 rounded-full overflow-hidden" aria-label="Zamana atla">
          <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${progress}%`, background: "var(--s-primary)" }} />
        </button>
        <span className="shrink-0 text-xs tabular-nums text-white/60">{fmtTime(cur)} / {fmtTime(dur)}</span>
      </div>
    );
  }

  if (style === "vinyl") {
    return (
      <div className="relative w-full rounded-lg px-3 py-2.5 overflow-hidden border border-white/10" style={{ background: "radial-gradient(circle at 30% 20%, rgba(212,175,55,0.14), transparent 60%), rgba(0,0,0,0.55)" }} data-testid="audio-player-vinyl">
        {commonAudio}
        <div className="flex items-center gap-3">
          <div className="relative shrink-0 w-12 h-12">
            {/* Vinyl disc */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background: "radial-gradient(circle, #1a1a1a 0%, #0a0a0a 60%, #1a1a1a 100%)",
                boxShadow: "0 0 0 1px rgba(255,255,255,0.05), 0 4px 14px rgba(0,0,0,0.5), inset 0 0 14px rgba(0,0,0,0.8)",
                animation: playing ? "vinyl-spin 4s linear infinite" : "none",
              }}
            >
              <div className="absolute inset-1 rounded-full border border-white/5" />
              <div className="absolute inset-2 rounded-full border border-white/5" />
            </div>
            <div
              className="absolute inset-[30%] rounded-full overflow-hidden border border-white/10 flex items-center justify-center bg-stone-800"
              style={{ animation: playing ? "vinyl-spin 4s linear infinite" : "none" }}
            >
              {cover ? (
                <Img src={cover} alt={title || "kapak"} className="w-full h-full object-cover" />
              ) : (
                <Music2 size={8} className="text-white/50" />
              )}
            </div>
            <div className="absolute left-1/2 top-1/2 w-1 h-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-black ring-1 ring-white/10" />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <div className="text-[13px] font-semibold truncate tracking-tight leading-tight" style={{ color: "var(--s-primary)" }}>{title || "Parça"}</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={toggle}
                className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition hover:scale-105 shadow"
                style={{ background: "var(--s-primary)", color: "var(--s-onprimary)" }}
                data-testid="audio-play-btn"
                aria-label={playing ? "Duraklat" : "Oynat"}
              >
                {playing ? <Pause size={11} /> : <Play size={11} className="ml-0.5" />}
              </button>
              <div className="flex-1 flex items-center gap-[1.5px] h-4 min-w-0">
                {Array.from({ length: 22 }).map((_, i) => {
                  const h = 6 + Math.round(Math.sin(i * 0.9) * 3 + Math.cos(i * 0.4) * 3);
                  const activeBar = (i / 22) * 100 < progress;
                  return (
                    <span
                      key={i}
                      className="flex-1 rounded-full transition-colors"
                      style={{
                        height: `${Math.max(3, h)}px`,
                        background: activeBar ? "var(--s-primary)" : "rgba(255,255,255,0.14)",
                      }}
                    />
                  );
                })}
              </div>
              <span className="shrink-0 text-[10px] tabular-nums text-white/60">{fmtTime(cur)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default: card
  return (
    <div className="flex items-center gap-5 rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-4 sm:p-5" data-testid="audio-player-card">
      {commonAudio}
      <div className="relative shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden bg-stone-800 border border-white/10 flex items-center justify-center">
        {cover ? <Img src={cover} alt={title || "kapak"} className="w-full h-full object-cover" /> : <Music2 size={26} className="text-white/40" />}
      </div>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="min-w-0">
          <div className="truncate text-base sm:text-lg font-semibold" style={{ color: "var(--s-primary)" }}>{title || "Parça"}</div>
          {artist && <div className="truncate text-xs text-white/60">{artist}</div>}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={toggle}
            className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition hover:scale-105 shadow"
            style={{ background: "var(--s-primary)", color: "var(--s-onprimary)" }}
            data-testid="audio-play-btn"
            aria-label={playing ? "Duraklat" : "Oynat"}
          >
            {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
          </button>
          <button type="button" onClick={seek} className="relative block h-1.5 flex-1 bg-white/10 rounded-full overflow-hidden" aria-label="Zamana atla">
            <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${progress}%`, background: "var(--s-primary)" }} />
          </button>
          <span className="shrink-0 text-xs tabular-nums text-white/60">{fmtTime(cur)} / {fmtTime(dur)}</span>
        </div>
      </div>
    </div>
  );
}

function MediaBlock({ block, t }) {
  const d = block.data || {};
  const kind = d.kind || "audio";
  const source = d.source || "url";
  const src = source === "upload" ? d.file_url : d.url;
  const desc = t(d.description);
  const detail = t(d.detail);
  const trackTitle = t(block.title) || (kind === "audio" ? "Parça" : "Video");
  const [showDetail, setShowDetail] = useState(false);
  if (!src) return null;

  let player = null;
  if (kind === "video") {
    const v = detectVideo(src);
    if (!v) return null;
    if (v.kind === "iframe") {
      player = (
        <div className="relative w-full aspect-video rounded-xl overflow-hidden border border-white/10 bg-black" data-testid="media-video-iframe">
          <iframe src={v.src} title={trackTitle} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className="absolute inset-0 w-full h-full" />
        </div>
      );
    } else {
      player = (
        <video src={v.src} controls className="w-full rounded-xl border border-white/10 bg-black" data-testid="media-video-file" />
      );
    }
  } else {
    const a = detectAudio(src);
    if (!a) return null;
    if (a.kind === "iframe") {
      const isSpot = /spotify\.com/.test(a.src);
      player = (
        <iframe
          src={a.src}
          title={trackTitle}
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
          loading="lazy"
          className={`w-full rounded-xl border border-white/10 ${isSpot ? "h-[152px]" : "h-[166px]"}`}
          data-testid="media-audio-iframe"
        />
      );
    } else {
      player = (
        <AudioPlayer
          src={a.src}
          title={trackTitle}
          artist={d.artist}
          cover={d.cover}
          style={d.player_style || "card"}
        />
      );
    }
  }

  return (
    <div className="w-full space-y-2">
      {player}
      {desc && (
        <p className="text-[11px] text-white/70 leading-relaxed rounded-md border border-white/5 bg-white/[0.035] px-2.5 py-1.5">
          {desc}
        </p>
      )}
      {detail && (
        <div data-testid="media-detail-wrap">
          <button
            type="button"
            onClick={() => setShowDetail((v) => !v)}
            className="inline-flex items-center gap-1 text-xs font-semibold tracking-wide transition hover:text-white"
            style={{ color: "var(--s-primary)" }}
            data-testid="media-detail-toggle"
            aria-expanded={showDetail}
          >
            {showDetail ? "Detayı kapat" : "Detay"}
            <span className={`transition-transform ${showDetail ? "rotate-180" : ""}`} aria-hidden>▾</span>
          </button>
          <div
            className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${showDetail ? "grid-rows-[1fr] opacity-100 mt-2" : "grid-rows-[0fr] opacity-0"}`}
            data-testid="media-detail"
          >
            <div className="overflow-hidden">
              <div className="text-sm text-white/80 leading-relaxed whitespace-pre-line rounded-lg border border-white/10 bg-white/[0.03] p-4">
                {detail}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CasesBlock({ block, t, fallbackCases }) {
  const items = (block.data?.useAll ? fallbackCases : block.data?.items) || fallbackCases || [];
  if (!items.length) return null;
  return (
    <>
      <BlockHeading title={block.title} t={t} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((c, i) => <CaseCard key={c.id || i} c={c} t={t} />)}
      </div>
    </>
  );
}

function ReferencesBlock({ block, t, fallbackRefs }) {
  const items = (block.data?.useAll ? fallbackRefs : block.data?.items) || fallbackRefs || [];
  if (!items.length) return null;
  return (
    <>
      <BlockHeading title={block.title} t={t} />
      <div className="grid md:grid-cols-2 gap-5">
        {items.map((q, i) => <TestimonialCard key={q.id || i} q={q} t={t} />)}
      </div>
    </>
  );
}

function DescriptionBlock({ block, t, service }) {
  const html = t(service?.description) || "";
  if (!html) return null;
  return (
    <>
      <BlockHeading title={block.title} t={t} />
      <div className="prose-site prose-lg prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: lazyImgs(html) }} />
    </>
  );
}

function HighlightsBlock({ block, t, service, onCta }) {
  const items = Array.isArray(service?.highlights) ? service.highlights : [];
  if (!items.length) return null;
  const layout = block.data?.layout || "sidebar";
  const ctaLabel = t(service?.cta_label);

  if (layout === "grid") {
    return (
      <>
        <BlockHeading title={block.title} t={t} />
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((h, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-4 flex items-start gap-3">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-s-primary shrink-0" />
              <span className="text-sm leading-relaxed text-white/85">{h}</span>
            </div>
          ))}
        </div>
      </>
    );
  }

  if (layout === "inline") {
    return (
      <>
        <BlockHeading title={block.title} t={t} />
        <ul className="flex flex-wrap gap-2">
          {items.map((h, i) => (
            <li key={i} className="rounded-full border border-white/15 bg-white/[0.06] backdrop-blur-md px-4 py-2 text-sm flex items-center gap-2 text-white/90">
              <span className="w-1.5 h-1.5 rounded-full bg-s-primary" />{h}
            </li>
          ))}
        </ul>
      </>
    );
  }

  // sidebar (default) — compact column on the inside
  return (
    <>
      <BlockHeading title={block.title} t={t} />
      <p className="eyebrow mb-4">Öne çıkan başlıklar</p>
      <ul className="space-y-2.5">
        {items.map((h, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-white/85">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-s-primary shrink-0" />
            <span>{h}</span>
          </li>
        ))}
      </ul>
      {ctaLabel && (
        <button
          type="button"
          onClick={onCta}
          className="mt-6 inline-flex items-center gap-1.5 w-full justify-center bg-s-primary text-s-onprimary rounded-full px-5 py-2.5 text-sm font-semibold hover:brightness-110 transition"
        >
          {ctaLabel} <ArrowRight size={14} />
        </button>
      )}
    </>
  );
}

function CompanyBlock({ block, t, service }) {
  const c = service?.company || {};
  const has = c.name || c.logo || c.address || c.phone || c.email || c.website || c.map_url || c.map_embed;
  if (!has) return null;
  const showMap = block.data?.show_map !== false && !!c.map_embed;
  const websiteDisplay = c.website
    ? c.website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")
    : "";

  // Minimal, borderless colophon: logo on the left, info on the right.
  // No "Firma Bilgileri" title, no eyebrow, no frame.
  return (
    <div data-testid="company-credit-card" className="font-sans">
      <div className="flex items-start gap-5 sm:gap-7">
        {/* Logo (or fallback icon) */}
        <div
          data-testid="company-logo-chip"
          className="shrink-0 w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center"
        >
          {c.logo ? (
            <Img src={c.logo} alt={c.name || "logo"} className="w-full h-full object-contain" />
          ) : (
            <Building2 size={40} className="text-s-muted" />
          )}
        </div>

        {/* Info column — plain typography, no boxes */}
        <div className="min-w-0 flex-1 space-y-1.5">
          {c.name && (
            <h3
              data-testid="company-name"
              className="font-heading text-lg sm:text-xl font-semibold text-s-text leading-tight"
            >
              {c.name}
            </h3>
          )}
          <div className="space-y-1 text-sm text-s-muted">
            {c.address && (
              <p data-testid="company-credit-address" className="flex items-start gap-2 leading-relaxed">
                <MapPin size={14} className="mt-0.5 shrink-0 opacity-60" />
                <span>{c.address}</span>
              </p>
            )}
            {c.phone && (
              <p className="flex items-center gap-2">
                <Phone size={14} className="shrink-0 opacity-60" />
                <a
                  data-testid="company-credit-phone"
                  href={`tel:${c.phone.replace(/\s+/g, "")}`}
                  className="hover:text-s-primary transition-colors"
                >
                  {c.phone}
                </a>
              </p>
            )}
            {c.email && (
              <p className="flex items-center gap-2">
                <Mail size={14} className="shrink-0 opacity-60" />
                <a
                  data-testid="company-credit-email"
                  href={`mailto:${c.email}`}
                  className="hover:text-s-primary transition-colors truncate"
                >
                  {c.email}
                </a>
              </p>
            )}
            {c.website && (
              <p className="flex items-center gap-2">
                <Globe size={14} className="shrink-0 opacity-60" />
                <a
                  data-testid="company-credit-website"
                  href={c.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-s-primary transition-colors inline-flex items-center gap-1"
                >
                  {websiteDisplay}
                  <ArrowUpRight size={12} />
                </a>
              </p>
            )}
            {c.map_url && (
              <p>
                <a
                  data-testid="company-credit-map-button"
                  href={c.map_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-s-primary hover:underline"
                >
                  Haritada aç <ArrowUpRight size={12} />
                </a>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Optional subtle map embed below (only when user opts in) */}
      {showMap && (
        <div className="mt-6 aspect-[21/9] rounded-xl overflow-hidden">
          <iframe
            data-testid="company-map-embed"
            src={c.map_embed}
            title="Harita"
            className="w-full h-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            allowFullScreen
          />
        </div>
      )}
    </div>
  );
}

/* =====================================================
   Editable overlay (used in admin live preview)
   ===================================================== */

const SPANS = [
  { key: "full",       label: "Tam",  hint: "12/12", cols: 12, icon: Square     },
  { key: "two_thirds", label: "2/3",  hint: "8/12",  cols: 8,  icon: Columns2   },
  { key: "half",       label: "1/2",  hint: "6/12",  cols: 6,  icon: Columns2   },
  { key: "third",      label: "1/3",  hint: "4/12",  cols: 4,  icon: Columns3   },
  { key: "quarter",    label: "1/4",  hint: "3/12",  cols: 3,  icon: Columns4   },
];

const BLOCK_TYPE_LABELS = {
  text: { icon: Type,       label: "Metin" },
  description: { icon: FileText, label: "Uzun açıklama" },
  highlights: { icon: Star, label: "Öne çıkanlar" },
  list: { icon: ListIcon,   label: "Liste" },
  image: { icon: ImageIcon, label: "Görsel" },
  gallery: { icon: Images,  label: "Galeri" },
  cases: { icon: Briefcase, label: "Çalışmalar" },
  references: { icon: Quote, label: "Referanslar" },
  company: { icon: Building2, label: "Firma kartı" },
  spacer: { icon: Minus,    label: "Boşluk" },
};

const ADD_TYPES = [
  { key: "text", label: "Metin", icon: Type },
  { key: "list", label: "Liste", icon: ListIcon },
  { key: "image", label: "Görsel", icon: ImageIcon },
  { key: "gallery", label: "Galeri", icon: Images },
  { key: "cases", label: "Çalışma kartları", icon: Briefcase },
  { key: "references", label: "Referanslar", icon: Quote },
  { key: "company", label: "Firma kartı", icon: Building2 },
  { key: "highlights", label: "Öne çıkanlar", icon: Star },
  { key: "spacer", label: "Boşluk", icon: Minus },
];

function StylePopover({ block, idx, onStyle }) {
  const [open, setOpen] = useState(false);
  const s = block.style || {};
  const bg = Number(s.bg ?? 10);
  const border = s.border || "subtle";
  const shadow = s.shadow !== false;
  const blur = s.blur || "md";
  const borderColor = s.border_color || "";
  const upd = (patch) => onStyle({ ...s, ...patch });
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 bg-stone-900/90 backdrop-blur text-white text-[11px] font-medium rounded-full px-2 py-1 border border-white/10 shadow-lg hover:bg-stone-800"
        data-testid={`live-block-style-${idx}`}
        title="Blok stili"
        aria-label="Blok stili"
      >
        <SlidersHorizontal size={11} />
      </button>
      {open && (
        <>
          <button type="button" className="fixed inset-0 z-30 cursor-default" onClick={() => setOpen(false)} aria-label="kapat" />
          <div
            className="absolute right-0 top-full mt-1 z-40 bg-white text-stone-900 border border-stone-200 rounded-xl shadow-xl p-3 w-[260px] space-y-3"
            data-testid={`live-block-style-popover-${idx}`}
          >
            {/* Opacity slider */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Transparanlık</label>
                <span className="font-mono text-[11px] text-stone-700">{bg}%</span>
              </div>
              <input
                type="range" min={0} max={100} value={bg}
                onChange={(e) => upd({ bg: Number(e.target.value) })}
                className="w-full accent-stone-900"
                data-testid={`live-block-style-bg-${idx}`}
              />
            </div>
            {/* Border mode */}
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Çerçeve</label>
              <div className="grid grid-cols-3 gap-1">
                {[
                  { k: "none", t: "Yok" },
                  { k: "subtle", t: "İnce" },
                  { k: "accent", t: "Vurgulu" },
                ].map((o) => (
                  <button
                    key={o.k}
                    type="button"
                    onClick={() => upd({ border: o.k })}
                    className={`px-2 py-1.5 rounded-md text-xs ${border === o.k ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}
                    data-testid={`live-block-style-border-${idx}-${o.k}`}
                  >
                    {o.t}
                  </button>
                ))}
              </div>
            </div>
            {/* Border color */}
            {border !== "none" && (
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Çerçeve rengi</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={borderColor || "#ffffff"}
                    onChange={(e) => upd({ border_color: e.target.value })}
                    className="w-9 h-9 rounded-md border border-stone-200 cursor-pointer bg-transparent"
                    data-testid={`live-block-style-bordercolor-${idx}`}
                  />
                  <input
                    type="text"
                    value={borderColor}
                    placeholder="#ffffff — varsayılana dön için boşalt"
                    onChange={(e) => upd({ border_color: e.target.value })}
                    className="flex-1 text-xs font-mono px-2 py-1.5 border border-stone-200 rounded-md"
                  />
                  {borderColor && (
                    <button
                      type="button"
                      onClick={() => upd({ border_color: "" })}
                      className="text-[10px] text-stone-500 hover:text-stone-900 px-1.5 py-1 rounded"
                      title="Varsayılan renge dön"
                    >
                      Sıfırla
                    </button>
                  )}
                </div>
              </div>
            )}
            {/* Blur */}
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Bulanıklık</label>
              <div className="grid grid-cols-4 gap-1">
                {["none", "sm", "md", "lg"].map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => upd({ blur: k })}
                    className={`px-2 py-1.5 rounded-md text-xs ${blur === k ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}
                    data-testid={`live-block-style-blur-${idx}-${k}`}
                  >
                    {k === "none" ? "Yok" : k}
                  </button>
                ))}
              </div>
            </div>
            {/* Shadow */}
            <label className="flex items-center gap-2 text-xs text-stone-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={shadow}
                onChange={(e) => upd({ shadow: e.target.checked })}
                className="w-4 h-4 accent-stone-900"
                data-testid={`live-block-style-shadow-${idx}`}
              />
              Gölge
            </label>
          </div>
        </>
      )}
    </div>
  );
}

function EditToolbar({ block, idx, cols, onSpan, onStyle, onDelete }) {
  const [open, setOpen] = useState(false);
  const current = SPANS.find((s) => s.cols === cols) || { label: `${cols}/12`, cols };
  const meta = BLOCK_TYPE_LABELS[block.type] || { icon: Square, label: block.type };
  const Icon = meta.icon;
  return (
    <div
      className="absolute inset-x-2 top-2 z-20 pointer-events-auto flex items-center justify-between gap-1.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
      data-testid={`live-block-toolbar-${idx}`}
    >
      <div className="flex items-center gap-1.5 bg-stone-900/90 backdrop-blur text-white text-[11px] font-medium rounded-full pl-1 pr-2.5 py-1 border border-white/10 shadow-lg">
        <button
          type="button"
          className="live-drag-handle cursor-grab active:cursor-grabbing p-1 rounded-full hover:bg-white/10"
          title="Sürükle"
          data-testid={`live-block-drag-${idx}`}
          aria-label="Sürükle"
        >
          <GripVertical size={13} />
        </button>
        <Icon size={12} className="opacity-80" />
        <span>{meta.label}</span>
      </div>
      <div className="flex items-center gap-1">
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1 bg-stone-900/90 backdrop-blur text-white text-[11px] font-medium rounded-full px-2.5 py-1 border border-white/10 shadow-lg hover:bg-stone-800"
            data-testid={`live-block-span-${idx}`}
            title={`Genişlik: ${cols}/12 — sağ kenardan sürükleyerek de ayarlayabilirsin`}
          >
            {cols}/12
          </button>
          {open && (
            <>
              <button type="button" className="fixed inset-0 z-30 cursor-default" onClick={() => setOpen(false)} aria-label="kapat" />
              <ul className="absolute right-0 top-full mt-1 z-40 bg-white text-stone-900 border border-stone-200 rounded-lg shadow-xl p-1 min-w-[160px]">
                <li className="px-3 py-1 text-[10px] uppercase tracking-wider text-stone-400">Hazır genişlikler</li>
                {SPANS.map((s) => {
                  const active = s.cols === cols;
                  return (
                    <li key={s.key}>
                      <button
                        type="button"
                        onClick={() => { onSpan(s.cols); setOpen(false); }}
                        className={`w-full flex items-center justify-between gap-3 px-3 py-1.5 rounded-md text-left text-xs hover:bg-stone-100 ${active ? "bg-stone-100 font-semibold" : ""}`}
                        data-testid={`live-block-span-${idx}-${s.key}`}
                      >
                        <span>{s.label}</span>
                        <span className="font-mono text-[10px] text-stone-400">{s.hint}</span>
                      </button>
                    </li>
                  );
                })}
                <li className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-stone-400">Hassas (1-12)</li>
                <li className="px-2 pb-1">
                  <input
                    type="range" min={1} max={12} step={1} value={cols}
                    onChange={(e) => onSpan(Number(e.target.value))}
                    className="w-full accent-stone-900"
                    data-testid={`live-block-span-${idx}-slider`}
                  />
                  <div className="flex items-center justify-between text-[10px] font-mono text-stone-500 px-0.5">
                    <span>1</span><span className="text-stone-900 font-semibold">{cols}/12</span><span>12</span>
                  </div>
                </li>
              </ul>
            </>
          )}
        </div>
        <StylePopover block={block} idx={idx} onStyle={onStyle} />
        <button
          type="button"
          onClick={onDelete}
          className="inline-flex items-center gap-1 bg-red-600/90 hover:bg-red-600 text-white text-[11px] font-medium rounded-full px-2 py-1 border border-white/10 shadow-lg"
          title="Sil"
          data-testid={`live-block-delete-${idx}`}
          aria-label="Sil"
        >
          <Trash2 size={11} />
        </button>
      </div>
    </div>
  );
}

function SortableCell({ block, idx, cols, isLast, children, onSpan, onStyle, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const colCls = COL_CLASS[cols] || COL_CLASS[12];
  const { cls: glassCls, style: glassStyle } = cellClassAndStyle(block.style);
  const wrapRef = useRef(null);
  const [resizing, setResizing] = useState(false);

  // Pointer-based resize from the right edge. 12-col snap, mobile untouched.
  // Dragging updates the span live via onSpan (which already triggers autosave upstream).
  const onResizeStart = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    const cell = wrapRef.current;
    if (!cell) return;
    const gridEl = cell.parentElement; // the 12-col grid
    const gridRect = gridEl.getBoundingClientRect();
    const gap = parseFloat(getComputedStyle(gridEl).columnGap || "0") || 0;
    const colW = (gridRect.width - gap * 11) / 12;
    const startX = ev.clientX;
    const startCols = cols;
    let lastEmitted = cols;
    setResizing(true);
    const onMove = (e) => {
      const dx = e.clientX - startX;
      const delta = Math.round(dx / (colW + gap));
      const next = Math.max(1, Math.min(12, startCols + delta));
      if (next !== lastEmitted) {
        lastEmitted = next;
        onSpan(next);
      }
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setResizing(false);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <div
      ref={(el) => { setNodeRef(el); wrapRef.current = el; }}
      style={style}
      className={`col-span-12 ${colCls} relative group ${isDragging ? "z-20" : ""}`}
      {...attributes}
      {...listeners}
      data-testid={`live-block-${idx}`}
    >
      <div
        className={`${glassCls} ${GLASS_PAD} outline outline-2 outline-dashed outline-transparent group-hover:outline-s-primary/60 transition-[outline-color] ${isDragging ? "ring-2 ring-s-primary shadow-2xl rotate-[0.5deg]" : ""} ${resizing ? "ring-2 ring-s-primary" : ""}`}
        style={glassStyle}
      >
        <EditToolbar block={block} idx={idx} cols={cols} onSpan={onSpan} onStyle={onStyle} onDelete={onDelete} />
        <div className="pt-8">{children}</div>
      </div>
      {/* Right-edge resize handle — hidden on mobile where cells always span full */}
      {!isLast && (
        <button
          type="button"
          onPointerDown={onResizeStart}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          className="hidden md:flex absolute top-1/2 -translate-y-1/2 -right-1 w-3 h-20 cursor-ew-resize z-30 items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity bg-s-primary/70 hover:bg-s-primary border border-white/30 shadow"
          title="Sürükleyerek genişliği ayarla"
          data-testid={`live-block-resize-${idx}`}
          aria-label="Genişliği sürükleyerek ayarla"
        >
          <span className="w-0.5 h-6 bg-white/80 rounded-full" aria-hidden />
        </button>
      )}
    </div>
  );
}

function AddBlockGhost({ onAdd }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="col-span-12 relative" data-testid="live-block-add-row">
      <div className="rounded-2xl border-2 border-dashed border-s-primary/40 bg-s-primary/[0.03] text-center py-6 hover:bg-s-primary/[0.07] transition-colors">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-2 text-sm font-medium text-s-primary hover:text-s-primary/80"
          data-testid="live-block-add-trigger"
        >
          <Plus size={16} /> Yeni blok ekle
        </button>
        {open && (
          <>
            <button type="button" className="fixed inset-0 z-30 cursor-default" onClick={() => setOpen(false)} aria-label="kapat" />
            <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-40 bg-white text-stone-900 border border-stone-200 rounded-xl shadow-xl p-2 w-[340px] grid grid-cols-3 gap-1" data-testid="live-block-add-menu">
              {ADD_TYPES.map((T) => {
                const Icon = T.icon;
                return (
                  <button
                    key={T.key}
                    type="button"
                    onClick={() => { onAdd(T.key); setOpen(false); }}
                    className="flex flex-col items-center justify-center gap-1 px-2 py-3 rounded-lg hover:bg-stone-100 text-[11px] font-medium"
                    data-testid={`live-block-add-${T.key}`}
                  >
                    <Icon size={16} className="text-s-primary" />
                    {T.label}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* =====================================================
   Dispatcher
   ===================================================== */

const RENDERERS = {
  heading: HeadingBlock,
  text: TextBlock,
  description: DescriptionBlock,
  highlights: HighlightsBlock,
  list: ListBlock,
  image: ImageBlock,
  gallery: GalleryBlock,
  media: MediaBlock,
  cases: CasesBlock,
  references: ReferencesBlock,
  company: CompanyBlock,
  spacer: () => null, // handled specially
};

export function BlockRenderer({ block, t, fallbackCases, fallbackRefs, service, onCta }) {
  const Cmp = RENDERERS[block?.type];
  if (!Cmp) return null;
  return <Cmp block={block} t={t} fallbackCases={fallbackCases} fallbackRefs={fallbackRefs} service={service} onCta={onCta} />;
}

/* =====================================================
   List wrapper — 12-col grid + liquid-glass cells
   Accepts optional edit-mode props:
     editable        – show hover toolbars & enable DnD
     onBlocksChange  – fn(newBlocksArray) called on reorder/span/delete
     onAddBlock      – fn(type) called when "+" picker chooses a type
   ===================================================== */

export default function ServiceBlocksList({ blocks, t, fallbackCases, fallbackRefs, service, onCta, editable = false, onBlocksChange, onAddBlock }) {
  const list = Array.isArray(blocks) ? blocks : [];
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  if (!list.length && !editable) return null;

  const handleDragEnd = ({ active, over }) => {
    if (!editable || !onBlocksChange || !over || active.id === over.id) return;
    const oldIdx = list.findIndex((b) => b.id === active.id);
    const newIdx = list.findIndex((b) => b.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onBlocksChange(arrayMove(list, oldIdx, newIdx));
  };
  const setSpan = (i, span) => {
    if (!onBlocksChange) return;
    const nx = [...list];
    nx[i] = { ...nx[i], span };
    onBlocksChange(nx);
  };
  const setStyle = (i, style) => {
    if (!onBlocksChange) return;
    const nx = [...list];
    nx[i] = { ...nx[i], style };
    onBlocksChange(nx);
  };
  const delBlock = (i) => {
    if (!onBlocksChange) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm("Bu bloğu silmek istediğine emin misin?")) return;
    const nx = [...list];
    nx.splice(i, 1);
    onBlocksChange(nx);
  };

  const grid = (
    <div className="grid grid-cols-12 gap-5 sm:gap-6">
      {list.map((b, i) => {
        if (!b) return null;

        // Spacer — just vertical whitespace, break the row (not sortable to keep DOM simple)
        if (b.type === "spacer" && !editable) {
          const h = Math.max(8, Math.min(Number(b.data?.height || 48), 240));
          return (
            <div key={b.id || i} className="col-span-12" aria-hidden style={{ height: `${h}px` }} />
          );
        }

        const span = b.span || "full";
        const inner = (
          <BlockRenderer
            block={b}
            t={t}
            fallbackCases={fallbackCases}
            fallbackRefs={fallbackRefs}
            service={service}
            onCta={onCta}
          />
        );

        if (editable) {
          const cols = spanToCols(span);
          const isLast = i === list.length - 1;
          // Spacer: show a short placeholder so it can still be reordered/deleted
          if (b.type === "spacer") {
            return (
              <SortableCell key={b.id} block={b} idx={i} cols={12} isLast={isLast} onSpan={(s) => setSpan(i, s)} onStyle={(st) => setStyle(i, st)} onDelete={() => delBlock(i)}>
                <div className="h-10 flex items-center justify-center text-xs text-white/50 italic">— boşluk ({b.data?.height || 48}px) —</div>
              </SortableCell>
            );
          }
          return (
            <SortableCell key={b.id} block={b} idx={i} cols={cols} isLast={isLast} onSpan={(s) => setSpan(i, s)} onStyle={(st) => setStyle(i, st)} onDelete={() => delBlock(i)}>
              {inner}
            </SortableCell>
          );
        }

        const colCls = spanClass(span);
        const { cls: pubCls, style: pubStyle } = cellClassAndStyle(b.style);
        const hasFrame = !!(b.style && Object.keys(b.style).length > 0);
        return (
          <div key={b.id || i} className={`col-span-12 ${colCls}`}>
            <div className={`${pubCls} ${hasFrame ? GLASS_PAD : ""} h-full`} style={pubStyle}>{inner}</div>
          </div>
        );
      })}
      {editable && onAddBlock && <AddBlockGhost onAdd={onAddBlock} />}
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 mt-16" data-testid="service-blocks">
      {editable ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={list.map((b) => b.id)} strategy={rectSortingStrategy}>
            {grid}
          </SortableContext>
        </DndContext>
      ) : grid}
    </div>
  );
}
